import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck2, MessageSquare, UserMinus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { wardScope } from "@/lib/patient";
import { Cell, DataTable, MetricCard, Panel, PanelHeader, Row, StatusBadge } from "./ui";
import { LEVEL_TONE, formatDay } from "./Conversations";
import { ConversationsOverviewPanel, CounselorFilter } from "./ConversationsOverview";

const PERIODS = [
  { value: "week", label: "Semana atual" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
];

export const useConversationMetrics = (period, ward, counselorId) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api
      .get("/portal/conversations/metrics", { params: { period, ward, counselor_id: counselorId || undefined } })
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, ward, counselorId]);
  return data;
};

export const ConversationMetricCards = ({ data, onOverdueClick }) => (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
    <MetricCard
      testId="conversations-avg-per-patient"
      label="Média de conversas por paciente/semana"
      value={data ? String(data.general.avg_per_patient_week).replace(".", ",") : "—"}
      hint={data ? `${data.general.conversations} conversa(s) no período` : ""}
      icon={MessageSquare}
    />
    <MetricCard
      testId="conversations-coverage"
      label="Cobertura semanal"
      value={data ? `${data.general.coverage_count} de ${data.general.active_patients} — ${data.general.coverage_percent}%` : "—"}
      hint="Pacientes ativos com conversa nos últimos 7 dias"
      icon={CalendarCheck2}
    />
    <button type="button" onClick={onOverdueClick} className="text-left" data-testid="conversations-overdue-button">
      <MetricCard
        testId="conversations-overdue"
        label={`Pacientes sem conversa há ${data?.general.alert_days || 14} dias ou mais`}
        value={data ? data.general.overdue_count : "—"}
        hint="Clique para ver os pacientes"
        icon={UserMinus}
        alert={(data?.general.overdue_count || 0) > 0}
      />
    </button>
  </div>
);

// Bloco completo de métricas de conversas (indicadores + visão por conselheiro).
export default function ConversationMetrics() {
  const { user } = useAuth();
  const scope = wardScope(user);
  const [period, setPeriod] = useState("7d");
  const [ward, setWard] = useState(scope.value);
  const [showOverdue, setShowOverdue] = useState(false);
  const [counselorId, setCounselorId] = useState("");
  const data = useConversationMetrics(period, ward, counselorId);

  return (
    <section data-testid="conversation-metrics-section" className="space-y-5">
      <Panel testId="conversation-metrics-panel" className="space-y-5">
        <PanelHeader
          label="Conversas com Conselheiros"
          title="Acompanhamento das conversas"
          hint={data ? `${data.general.period_label} · ${formatDay(data.general.start)} a ${formatDay(data.general.end)}` : "Carregando…"}
          actions={
            <div className="flex flex-wrap gap-2">
              <CounselorFilter
                value={counselorId}
                onChange={setCounselorId}
                ward={ward}
                testId="conversations-metrics-counselor-filter"
              />
              <select
                data-testid="conversations-period-filter"
                aria-label="Período"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="h-10 rounded-xl border border-border bg-white px-3 text-[14px]"
              >
                {PERIODS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              {scope.locked ? (
                <span
                  data-testid="conversations-ward-scope"
                  className="inline-flex h-10 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700"
                >
                  {scope.label}
                </span>
              ) : (
                <select
                  data-testid="conversations-ward-filter"
                  aria-label="Ala"
                  value={ward}
                  onChange={(e) => setWard(e.target.value)}
                  className="h-10 rounded-xl border border-border bg-white px-3 text-[14px]"
                >
                  <option value="all">Todas as alas</option>
                  <option value="MASCULINA">Masculina</option>
                  <option value="FEMININA">Feminina</option>
                </select>
              )}
            </div>
          }
        />

        <ConversationMetricCards data={data || null} onOverdueClick={() => setShowOverdue((v) => !v)} />

        {showOverdue ? (
          <div data-testid="conversations-overdue-list" className="space-y-2">
            {(data?.overdue_patients || []).length === 0 ? (
              <p className="text-[14px] text-ink-500">Nenhum paciente em alerta.</p>
            ) : (
              data.overdue_patients.map((row) => (
                <Link
                  key={row.patient_id}
                  to={`/portal/pacientes/${row.patient_id}`}
                  data-testid={`conversations-overdue-item-${row.patient_id}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-white p-3 hover:bg-sand"
                >
                  <span className="text-[14.5px] font-semibold text-ink-900">{row.patient_name}</span>
                  <span className="text-[13.5px] text-ink-600">{row.label}</span>
                  <StatusBadge tone={LEVEL_TONE[row.level]}>{row.level_label}</StatusBadge>
                </Link>
              ))
            )}
          </div>
        ) : null}
      </Panel>

      <ConversationsOverviewPanel ward={ward} counselorId={counselorId} />

      <Panel testId="conversation-by-counselor-panel" className="space-y-4">
        <PanelHeader label="Conversas" title="Desempenho por conselheiro" hint="Somente pacientes ativos da ala autorizada." />
        {(data?.by_counselor || []).length === 0 ? (
          <p data-testid="conversation-by-counselor-empty" className="text-[14px] text-ink-500">
            Nenhum resultado encontrado para os filtros selecionados.
          </p>
        ) : (
          <DataTable
            testId="conversation-counselor-table"
            columns={[
              { key: "name", label: "Conselheiro" },
              { key: "patients", label: "Pacientes" },
              { key: "conversations", label: "Conversas" },
              { key: "avg", label: "Média/paciente" },
              { key: "coverage", label: "Cobertura" },
              { key: "overdue", label: `≥${data.general.alert_days} dias` },
            ]}
          >
            {data.by_counselor.map((row) => (
              <Row key={row.counselor_id} testId={`conversation-counselor-${row.counselor_id}`}>
                <Cell>{row.counselor_name}</Cell>
                <Cell>{row.patients}</Cell>
                <Cell>{row.conversations}</Cell>
                <Cell>{String(row.avg_per_patient).replace(".", ",")}</Cell>
                <Cell>{row.coverage_percent}%</Cell>
                <Cell className={row.overdue_count > 0 ? "font-semibold text-danger" : ""}>{row.overdue_count}</Cell>
              </Row>
            ))}
          </DataTable>
        )}
      </Panel>
    </section>
  );
}
